import { ClerkProvider } from '@clerk/clerk-react';
import { useMantineTheme } from '@tf/ui';
import type React from 'react';
import { useEffect } from 'react';

import { addTFConsoleMethods } from '../tfConsole';

import { useGetAuthToken } from './useGetAuthToken';

const InitAuthRelatedConsoleCommands = () => {
	const getToken = useGetAuthToken();

	useEffect(() => {
		addTFConsoleMethods({
			printToken: () => {
				getToken().then((token) => {
					console.log(token);
				});
			},
		});
	}, [getToken]);

	return null;
};

type RouterFn = (to: string) => unknown;
type Navigation = { routerPush: RouterFn; routerReplace: RouterFn };
// Clerk takes both router functions or neither
type NavigationProps = Navigation | { routerPush?: never; routerReplace?: never };

interface Props {
	publishableKey: string;
	// without these Clerk switches sign-in steps with a full page reload
	navigation?: Navigation;
}

export const AuthProvider: React.FC<React.PropsWithChildren<Props>> = ({ publishableKey, navigation, children }) => {
	const { colors } = useMantineTheme();
	const navigationProps: NavigationProps = navigation ?? {};

	return (
		<ClerkProvider
			{...navigationProps}
			appearance={{
				variables: {
					colorPrimary: colors.brand[6],
				},
				elements: {
					card: {
						backgroundColor: '#fff',
						boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 20px -4px',
						borderColor: colors.gray[2],
					},
					cardBox: {
						borderWidth: 0.5,
						borderRadius: '8px',
						boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 20px -4px',
					},
					headerSubtitle: {
						display: 'none',
					},
					formFieldInput: {
						padding: '0.625rem 1rem',
						'&:focus': {
							boxShadow: 'rgb(151, 185, 248) 0px 0px 0px 1px !important',
						},
					},
					formButtonPrimary: {
						padding: '0.625rem 1rem',
						background: `${colors.brand[6]} !important`,
						boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 20px -4px',
						textTransform: 'uppercase',
						fontSize: '11px',
						fontWeight: 'bold',
						'& svg': {
							display: 'none',
						},
						'&::after': {
							display: 'none',
						},
						'&:hover': {
							backgroundColor: `${colors.brand[7]} !important`,
						},
					},
				},
			}}
			publishableKey={publishableKey}
			localization={{
				signUp: {
					continue: {
						title: 'Please set your password',
					},
				},
			}}
		>
			<InitAuthRelatedConsoleCommands />
			{children}
		</ClerkProvider>
	);
};
